import { types as coreTypes } from '@sitebuilder/core';

export interface ISlidedeckApiClient {
    createNewSlidedeck(
        libraryName: string,
        slidedeckId: string,
        slidedeck: coreTypes.Slidedeck,
        options?: { originalHostname?: string; accessToken?: string }
    ): Promise<void>;

    saveSlidedeck(
        libraryName: string,
        slidedeckId: string,
        slidedeck: coreTypes.Slidedeck,
        viewUrl: string,
        options?: { originalHostname?: string; accessToken?: string }
    ): Promise<void>;

    getSlidedeck(
        libraryName: string,
        slidedeckId: string,
        options?: { originalHostname?: string; accessToken?: string }
    ): Promise<coreTypes.Slidedeck>;

    listSlidedecks(
        libraryName: string,
        folderName: string,
        getDeleted: boolean,
        options?: { originalHostname?: string; accessToken?: string },
        filters?: { [key: string]: string },
        sort?: { field?: string; dir?: string }
    ): Promise<any[]>;

    getSlidedeckVersions(
        libraryName: string,
        slidedeckId: string,
        options?: { originalHostname?: string; accessToken?: string }
    ): Promise<any[]>;

    revertVersion(
        libraryName: string,
        slidedeckName: string,
        versionNumber: any,
        options?: { originalHostname?: string; accessToken?: string }
    ): Promise<any[]>;
}

export class SlidedeckApiClient implements ISlidedeckApiClient {
    baseUrl: string;
    constructor(baseUrl: string = '') {
        this.baseUrl = baseUrl;
    }

    private getHeaders(
        h: {
            [key: string]: string;
        },
        options?: { accessToken?: string }
    ) {
        const headers: {
            [key: string]: string;
        } = {};

        if (typeof options?.accessToken === 'string' && options.accessToken.length > 0) {
            headers.Authorization = `bearer ${options.accessToken}`;
        }

        return { ...headers, ...h };
    }

    async createNewSlidedeck(
        libraryName: string,
        slidedeckId: string,
        slidedeck: coreTypes.Slidedeck,
        options?: { originalHostname?: string; accessToken?: string }
    ) {
        const url = `${this.baseUrl || ''}/api/admin/slidedecks/${libraryName}/${slidedeckId}`;

        // set the etag to an empty string, so save succeeds only if
        // another Slidedeck with same name does not exist.
        slidedeck.etag = '';

        await fetch(url, {
            method: 'POST',
            headers: this.getHeaders(
                {
                    'Content-Type': 'application/json',
                },
                options
            ),
            body: JSON.stringify(slidedeck),
        });
    }

    async saveNewFolder(
        folderName: string,
        options?: { originalHostname?: string; accessToken?: string }
    ) {
        const url = `${this.baseUrl || ''}/api/admin/slidedecks/create-folder/${folderName}`;

        await fetch(url, {
            method: 'POST',
            headers: this.getHeaders(
                {
                    'Content-Type': 'application/json',
                    'ldr-type': 'collection',
                },
                options
            ),
        });
    }

    async saveSlidedeck(
        libraryName: string,
        slidedeckId: string,
        slidedeck: coreTypes.Slidedeck,
        viewUrl: string,
        options?: { originalHostname?: string; accessToken?: string }
    ) {
        const url = `${this.baseUrl || ''}/api/admin/slidedecks/${libraryName}/${slidedeckId}`;

        await fetch(url, {
            method: 'POST',
            headers: this.getHeaders(
                {
                    'Content-Type': 'application/json',
                },
                options
            ),
            body: JSON.stringify({ slidedeck, viewUrl }),
        });
    }

    async getSlidedeck(
        libraryName: string,
        slidedeckId: string,
        options?: { originalHostname?: string; accessToken?: string }
    ) {
        const url = `${this.baseUrl || ''}/api/slidedecks/${libraryName}/${slidedeckId}`;

        const res = await fetch(url, {
            method: 'GET',
            headers: this.getHeaders({}, options),
        });

        const slidedeck = await res.json();
        return slidedeck as coreTypes.Slidedeck;
    }

    async listSlidedecks(
        libraryName: string,
        folderName: string,
        getDeleted: boolean,
        options?: { originalHostname?: string; accessToken?: string },
        filters?: { [key: string]: string | Record<string, unknown> | string[] },
        sort?: { field?: string; dir?: string },
        take?: number,
        skip?: number
    ) {
        const url = folderName
            ? `${this.baseUrl || ''}/api/admin/slidedecks/list/${libraryName}${folderName}`
            : `${this.baseUrl || ''}/api/admin/slidedecks/${libraryName}`;

        const res = await fetch(url, {
            method: 'POST',
            headers: this.getHeaders(
                {
                    'Content-Type': 'application/json',
                },
                options
            ),
            body: JSON.stringify({
                deleted: getDeleted,
                sortBy: sort?.field || 'Id',
                sortDirection: sort?.dir || 'asc',
                take: take || 25,
                skip: skip || 0,
                ...filters,
            }),
        });

        const slidedecks = await res.json();
        return slidedecks;
    }

    async getSlidedeckVersions(
        libraryName: string,
        slidedeckId: string,
        options?: { originalHostname?: string; accessToken?: string }
    ) {
        const url = `${
            this.baseUrl || ''
        }/api/admin/slidedecks/versions/${libraryName}/${slidedeckId}`;

        const res = await fetch(url, {
            method: 'GET',
            headers: this.getHeaders(
                {
                    'Content-Type': 'application/json',
                },
                options
            ),
        });

        const slidedeckVersions = await res.json();
        return slidedeckVersions;
    }

    async revertVersion(
        libraryName: string,
        slidedeckName: string | null,
        versionNumber: any,
        options?: { originalHostname?: string; accessToken?: string }
    ) {
        const url = `${
            this.baseUrl || ''
        }/api/admin/slidedecks/versions/revert/${libraryName}/${slidedeckName}/${versionNumber}`;

        const res = await fetch(url, {
            method: 'POST',
            headers: this.getHeaders(
                {
                    'Content-Type': 'application/json',
                },
                options
            ),
        });

        const slidedeckVersions = await res.json();
        return slidedeckVersions;
    }
}
